import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  Button,
  Input,
  FormControl,
  Text,
  Stack,
  Box,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Flex,
  IconButton,
  Tag,
  VStack,
  HStack,
  TagLabel,
  TagCloseButton,
 
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import { KGStepInterface } from "./Interface";
import KGAdditionalContext from "./KGAdditionalContext";
import { FilterProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { KGObservationProto, ObservationEditType, RecommendationRuleProto } from "build-ts/analysis_plan";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import KGBenchmarkConfig from "./KGBenchmarkConfig";
import { ColumnType, AnalysisColumnType, MetadataTypeEnum, TimeBucket } from "build-ts/enums";
import debounce from "lodash.debounce";
import { formatToSentenceCase} from "utils/nameUtils";
import showToast from "hooks/useCustomToast";
import { getKGColumnValues } from "services/DataModelService";
import ColumnValues from "components/common/ColumnValues";
import { getNodeData } from "services/AnalysisTreeServices";
import { AnalysisTreeNodeProto } from "build-ts/analysis";

const KGNodeConfigurator: React.FC = () => {
  const {
    selectedNodeId,
    configurator,
    nodeMap,
    handleNodeSave,
    handleGetDataForNode,
    setObservationData,
  } = useKGAnalysisPlanContext();

  const [nodeName, setNodeName] = useState<string>("");
  const [currentNode, setCurrentNode] = useState<KGStepInterface | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<ObservationColumnProto[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<MetricProto[]>([]);
  const [filters, setFilters] = useState<FilterProto[]>([]);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<BenchmarkEntityProto[]>([]);
  const [analysisColumns, setAnalysisColumns] = useState<ColumnHashProto[]>([]);
  const [recommendationRules, setRecommendationRules] = useState<RecommendationRuleProto[]>([]);
  const [availableDescendants, setAvailableDescendants] = useState<Array<{
    id: string;
    name: string;
    selected: boolean;
  }>>([]);
  const [filterToPropagate, setFilterToPropagate] = useState<FilterProto | null>(null);

  // State for column dropdown selection
  const [selectedColumn, setSelectedColumn] = useState<Header | null>(null);
  // State for metric dropdown selection  
  const [selectedMetric, setSelectedMetric] = useState<MetricProto | null>(null);
  
  // Flag to track if we're initializing (loading node data) vs user making changes
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Flag to prevent auto-save during save operations
  const [isSaving, setIsSaving] = useState(false);
  
  // Loading state for Get Data operation
  const [isGettingData, setIsGettingData] = useState<boolean>(false);
  
  // State for recommended filters when Get Data is clicked
  const [showRecommendedFilters, setShowRecommendedFilters] = useState<boolean>(false);
  const [recommendedFiltersToApply, setRecommendedFiltersToApply] = useState<FilterProto[]>([]);
  const [recommendedFilterValues, setRecommendedFilterValues] = useState<Map<string, string[]>>(new Map());
  const [selectedRecommendedFilters, setSelectedRecommendedFilters] = useState<Map<string, string[]>>(new Map());

  // Refs to track previous values for change detection
  const previousValuesRef = useRef({
    nodeName: "",
    selectedColumns: [] as ObservationColumnProto[],
    selectedMetrics: [] as MetricProto[],
    filters: [] as FilterProto[],
    selectedBenchmarks: [] as BenchmarkEntityProto[],
    analysisColumns: [] as ColumnHashProto[],
    recommendationRules: [] as RecommendationRuleProto[],
  });

  // Get available metrics from currentNode that aren't already selected
  const availableMetrics = useMemo(() => {
    if (!currentNode?.metrics) return [];
    
    // Filter out metrics that are already selected
    const selectedMetricIds = new Set(selectedMetrics.map(m => m.header?.id));
    return currentNode.metrics.filter(m => !selectedMetricIds.has(m.header?.id));
  }, [currentNode?.metrics, selectedMetrics]);

  // Get non-metric columns for the column dropdown (exclude metrics)
  const nonMetricColumns = useMemo(() => {
    if (!currentNode?.non_benchmarks) return [];
    
    return currentNode.non_benchmarks.filter(col => 
      col.kg_column_header?.type !== MetadataTypeEnum.METRIC
    );
  }, [currentNode?.non_benchmarks]);

  // Extract recommended columns from recommendation_edits
  const recommendedColumns = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];
    
    const columnEdits: ObservationColumnProto[] = [];
    
    // Process recommendation edits
    currentNode?.recommendation_edits?.forEach(edit => {
      // Process select_anyone field
      if (edit?.select_anyone) {
        edit.select_anyone.forEach(columnId => {
          // Only add non-metric columns to recommendedColumns
          const col = nonMetricColumns.find(col => col.column_id === columnId);
          if (col) {
            columnEdits.push(col);
          }
        });
      }
      
      // Process edits field for backward compatibility
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_COLUMN 
            && observationEdit.column
            && observationEdit.column.kg_column_header?.type !== MetadataTypeEnum.METRIC) {
            columnEdits.push(observationEdit.column);
          }
        });
      }
    });
    
    return columnEdits;
  }, [currentNode?.recommendation_edits, nonMetricColumns]);

  // Extract recommended metrics from recommendation_edits  
  const recommendedMetrics = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];
    
    const metricEdits: MetricProto[] = [];
    
    // Process recommendation edits
    currentNode?.recommendation_edits?.forEach(edit => {
      // Process select_anyone field for metrics
      if (edit?.select_anyone) {
        edit.select_anyone.forEach(columnId => {
          const metric = currentNode.metrics?.find(metric => metric.header?.id === columnId);
          if (metric) {
            metricEdits.push(metric);
          }
        });
      }
      
      // Process edits field for backward compatibility
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_METRIC 
            && observationEdit.metric) {
            metricEdits.push(observationEdit.metric);
          }
        });
      }
    });
    
    return metricEdits;
  }, [currentNode?.recommendation_edits, currentNode?.metrics]);

  // Extract recommended filters from recommendation_edits
  const recommendedFilters = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];
    
    const filterEdits: FilterProto[] = [];
    
    // Extract filters from ADD_FILTER edits
    currentNode?.recommendation_edits?.forEach(edit => {
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_FILTER 
            && observationEdit.filter) {
            filterEdits.push(observationEdit.filter);
          }
        });
      }
    });
    
    return filterEdits;
  }, [currentNode?.recommendation_edits]);

  // Initialize state when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId) {
      const node = nodeMap?.get(selectedNodeId);
      if (node) {
        setIsInitializing(true);
        
        setCurrentNode(node);
        setNodeName(node.label);
        setSelectedColumns(node.selected_columns || []);
        setSelectedMetrics(node.selected_metrics || []);
        setSelectedBenchmarks(node.selected_benchmarks || []);
        setAnalysisColumns(node.analysis_columns || []);
        setFilters(node.selected_filters || []);
        setRecommendationRules(node.recommendation_rules || []);
        
        // Update previous values ref to current values to prevent auto-save trigger
        previousValuesRef.current = {
          nodeName: node.label,
          selectedColumns: [...(node.selected_columns || [])],
          selectedMetrics: [...(node.selected_metrics || [])],
          filters: [...(node.selected_filters || [])],
          selectedBenchmarks: [...(node.selected_benchmarks || [])],
          analysisColumns: [...(node.analysis_columns || [])],
          recommendationRules: [...(node.recommendation_rules || [])],
        };
        
        // Mark initialization as complete
        setIsInitializing(false);
      } else {
        setCurrentNode(null);
      }
    }
  }, [selectedNodeId, nodeMap]);

  // Effect to update analysis_columns when selected columns or benchmarks change
  useEffect(() => {
    if (!currentNode || isInitializing) return;
    
    // Only include column hashes that have analysis_column_type for the analysis_columns field
    const updatedAnalysisColumns: ColumnHashProto[] = [];
    
    // Get all column IDs (from both selected columns and metrics)
    const columnIds: string[] = [];
    
    selectedColumns?.forEach(column => {
      if (column.column_id 
        && column.kg_column_header?.column_type !== ColumnType.ATTRIBUTE
        && column.kg_column_header?.column_type !== ColumnType.TEMPORAL) {
        columnIds.push(column.column_id);
      }
    });
    
    selectedMetrics?.forEach(metric => {
      if (metric.header?.id) {
        columnIds.push(metric.header.id);
      }
    });
    
    // Only add column hashes with analysis_column_type to analysis_columns
    // TODO: This contraint should be removed. In recommendation we should be able to add recommendations for actuals also.
    if (selectedBenchmarks.length > 0) {
      columnIds?.forEach(columnId => {
        selectedBenchmarks?.forEach(benchmark => {
          if (benchmark.header?.id) {
            // Find existing analysis types used in column hashes for this benchmark
            const existingAnalysisTypes = new Set<AnalysisColumnType>();
            
            analysisColumns?.forEach(col => {
              if (col.benchmark_id === benchmark.header?.id && 
                  col.analysis_column_type !== undefined) {
                existingAnalysisTypes.add(col.analysis_column_type);
              }
            });
            
            // Only add column hashes that have analysis_column_type
            existingAnalysisTypes?.forEach(analysisType => {
              // Check if this specific combination already exists
              const existingAnalysis = analysisColumns?.find(col => 
                col.actuals_id === columnId && 
                col.benchmark_id === benchmark.header?.id &&
                col.analysis_column_type === analysisType
              );
              
              if (existingAnalysis) {
                updatedAnalysisColumns.push(existingAnalysis);
              } else {
                updatedAnalysisColumns.push(
                 ColumnHashProto.create({
                   actuals_id: columnId,
                   benchmark_id: benchmark.header?.id,
                   analysis_column_type: analysisType
                 })
               );
              }
            });
          }
        });
      });
    }
    
    // Finally, update the analysis columns state (only includes hashes with analysis_column_type)
    setAnalysisColumns(updatedAnalysisColumns);
  }, [currentNode?.id, selectedColumns, selectedMetrics, selectedBenchmarks, isInitializing]);

  // Function to detect if any values have actually changed
  const hasActualChanges = useCallback((): boolean => {
    if (isInitializing || !currentNode) return false;

    const current = {
      nodeName,
      selectedColumns,
      selectedMetrics,
      filters,
      selectedBenchmarks,
      analysisColumns,
      recommendationRules,
    };

    const previous = previousValuesRef.current;

    // Deep comparison for each field
    const nodeNameChanged = current.nodeName !== previous.nodeName;
    
    const columnsChanged = current.selectedColumns.length !== previous.selectedColumns.length ||
      current.selectedColumns.some((col, idx) => 
        col.column_id !== previous.selectedColumns[idx]?.column_id ||
        col.time_bucket !== previous.selectedColumns[idx]?.time_bucket);
    
    const metricsChanged = current.selectedMetrics.length !== previous.selectedMetrics.length ||
      current.selectedMetrics.some((metric, idx) => 
        metric.header?.id !== previous.selectedMetrics[idx]?.header?.id);
    
    const filtersChanged = current.filters.length !== previous.filters.length ||
      current.filters.some((filter, idx) => 
        filter.filter_id !== previous.filters[idx]?.filter_id);
    
    const benchmarksChanged = current.selectedBenchmarks.length !== previous.selectedBenchmarks.length ||
      current.selectedBenchmarks.some((benchmark, idx) => 
        benchmark.header?.id !== previous.selectedBenchmarks[idx]?.header?.id);
    
    const analysisColumnsChanged = current.analysisColumns.length !== previous.analysisColumns.length ||
      current.analysisColumns.some((col, idx) => 
        col.actuals_id !== previous.analysisColumns[idx]?.actuals_id ||
        col.benchmark_id !== previous.analysisColumns[idx]?.benchmark_id ||
        col.analysis_column_type !== previous.analysisColumns[idx]?.analysis_column_type);
    
    const recommendationRulesChanged = current.recommendationRules.length !== previous.recommendationRules.length;

    const hasChanges = nodeNameChanged || columnsChanged || metricsChanged || filtersChanged || 
                      benchmarksChanged || analysisColumnsChanged || recommendationRulesChanged;

    return hasChanges;
  }, [nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, recommendationRules, isInitializing, currentNode]);

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce(() => {
      if (hasActualChanges() && !isSaving && currentNode) {        
        setIsSaving(true);
        
        const nodeInfo = {
          nodeName,
          selectedColumns,
          selectedBenchmarks,
          filters,
          analysisColumns,
          recommendationRules,
          selectedMetrics
        };
        
        handleNodeSave(currentNode?.id, nodeInfo);
        
        // Update previous values after successful save
        previousValuesRef.current = {
          nodeName,
          selectedColumns: [...selectedColumns],
          selectedMetrics: [...selectedMetrics],
          filters: [...filters],
          selectedBenchmarks: [...selectedBenchmarks],
          analysisColumns: [...analysisColumns],
          recommendationRules: [...recommendationRules],
        };
        
        setIsSaving(false);
      }
    }, 500), // 500ms delay for auto-save
    [isSaving, currentNode, nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, recommendationRules]
  );

  // Single effect to trigger auto-save when any relevant state changes
  useEffect(() => {
    if (!isInitializing && !isSaving && currentNode) {
      debouncedAutoSave();
    }
  }, [nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, recommendationRules, debouncedAutoSave, isInitializing, isSaving, currentNode]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  // Check if a column is in the recommended columns list
  const isRecommendedColumn = (columnId: string): boolean => {
    return recommendedColumns.some(col => 
      col.column_id === columnId || 
      col.kg_column_header?.id === columnId);
  };

  // Check if a metric is in the recommended metrics list
  const isRecommendedMetric = (metricId: string): boolean => {
    return recommendedMetrics.some(metric => 
      metric.header?.id === metricId);
  };


  // Generic handleSelectColumn that works for all column types
  const handleSelectColumn = (column: ObservationColumnProto) => {
    if (selectedColumns.some(c => c.column_id === column.column_id)) return;

    // For temporal columns, add with default time bucket (can be edited later)
    if (column.kg_column_header?.column_type === ColumnType.TEMPORAL) {
      setSelectedColumns(prev => [...prev, { ...column, time_bucket: TimeBucket.MONTHLY }]);
    } else {
      setSelectedColumns(prev => [...prev, column]);
    }
    setSelectedColumn(null);
  };

  // Handle metric selection from dropdown
  const handleSelectMetric = (metric: MetricProto) => {
    setSelectedMetric(metric);
    // Check if metric is already selected
    if (!selectedMetrics.some(m => m.header?.id === metric.header?.id)) {
      setSelectedMetrics(prev => [...prev, metric]);
    }
    // Reset selected metric to allow further selections
    setSelectedMetric(null);
  };

  // Remove a column from selection
  const handleRemoveColumn = (columnId: string) => {
    setSelectedColumns(prev => prev.filter(column => column.column_id !== columnId));
  };

  // Remove a metric from selection
  const handleRemoveMetric = (metricId: string) => {
    setSelectedMetrics(prev => prev.filter(metric => metric.header?.id !== metricId));
  };

  // Handle time bucket change for temporal columns
  const handleTimeBucketChange = (columnId: string, timeBucket: TimeBucket) => {
    setSelectedColumns(prev => 
      prev.map(column => 
        column?.column_id === columnId 
          ? { ...column, time_bucket: timeBucket }
          : column
      )
    );
  };

  //propogate filters to all nodes
  const handlePropogateFilterClick = (filter: FilterProto, currentNodeId: string) => {
    // Get all nodes from the nodeMap, excluding the current node
    const allNodeIds = Array.from(nodeMap.keys()).filter(id => id !== currentNodeId);

    const allNodes = allNodeIds.map((id) => {
      const node = nodeMap.get(id);
      return {
        id,
        name: node?.label || `Node ${id}`,
        selected: true,
      };
    });
  
    setAvailableDescendants(allNodes);
    setFilterToPropagate(filter);
  };
  

  // Get KG observation data for the current node
  const handleGetData = async () => {
    if (!currentNode) {
      showToast("No node selected", "", "error");
      return;
    }

    // Check for recommended filters
    const recommendedFiltersFromEdits: FilterProto[] = [];
    currentNode?.recommendation_edits?.forEach(edit => {
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_FILTER 
            && observationEdit.filter) {
            recommendedFiltersFromEdits.push(observationEdit.filter);
          }
        });
      }
    });

    // If there are recommended filters, show them for selection
    if (recommendedFiltersFromEdits.length > 0) {
      setRecommendedFiltersToApply(recommendedFiltersFromEdits);
      setShowRecommendedFilters(true);
      // Fetch values for each recommended filter
      await fetchRecommendedFilterValues(recommendedFiltersFromEdits);
      return;
    }

    // If no recommended filters, proceed with getting data
    setIsGettingData(true);
    await handleGetDataForNode(currentNode.id);
    setIsGettingData(false);
  };

  // Function to fetch values for recommended filters
  const fetchRecommendedFilterValues = async (recommendedFilters: FilterProto[]) => {
    const newFilterValues = new Map<string, string[]>();
    const newSelectedFilters = new Map<string, string[]>();
    
    for (const filter of recommendedFilters) {
      const columnId = filter.kg_column_header?.id;
      if (columnId) {
        try {
          const values = await getKGColumnValues(columnId);
          newFilterValues.set(columnId, values || []);
          // Initialize with empty selection
          newSelectedFilters.set(columnId, []);
        } catch (error) {
          console.error("Error fetching values for recommended filter:", error);
          newFilterValues.set(columnId, []);
          newSelectedFilters.set(columnId, []);
        }
      }
    }
    
    setRecommendedFilterValues(newFilterValues);
    setSelectedRecommendedFilters(newSelectedFilters);
  };

  // Function to apply recommended filters and get data (without updating node state)
  const handleApplyRecommendedFilters = async () => {
    if (!currentNode) return;

    // Create filters array with existing filters plus selected recommended filters
    const appliedFilters = [...(currentNode.selected_filters || [])];
    
    // Add selected recommended filters
    recommendedFiltersToApply.forEach(filter => {
      const columnId = filter.kg_column_header?.id;
      if (columnId && selectedRecommendedFilters.has(columnId)) {
        const selectedValues = selectedRecommendedFilters.get(columnId) || [];
        if (selectedValues.length > 0) {
          appliedFilters.push({
            ...filter,
            values: selectedValues,
          });
        }
      }
    });

    // Create KG observation with applied filters (don't update node state)
    const kgObservation = KGObservationProto.create({
      metrics: currentNode?.selected_metrics || [],
      benchmarks: currentNode?.selected_benchmarks || [],
      filters: appliedFilters,
      columns: currentNode?.selected_columns || []
    });

    // Hide the recommended filters section
    setShowRecommendedFilters(false);
    setRecommendedFiltersToApply([]);
    setRecommendedFilterValues(new Map());
    setSelectedRecommendedFilters(new Map());
    setIsGettingData(true);
    const request = AnalysisTreeNodeProto.create({
      id: currentNode.id,
      node_label: currentNode.label,
      node_depth: currentNode.node_depth,
      kg_observation: kgObservation,
      analysis_columns: currentNode?.analysis_columns || [],
    });
    // Get data with the KG observation containing applied filters
    const response = await getNodeData(request);
    if (response) {
      // Create NodeInsights structure that DataTableMKG expects
      setObservationData({current_node: response});
      configurator.onClose();
    } else {
      showToast("Failed to fetch observation data", "", "error");
    }
    setIsGettingData(false);
  };

  // Function to skip recommended filters and get data with current filters
  const handleSkipRecommendedFilters = async () => {
    setShowRecommendedFilters(false);
    setRecommendedFiltersToApply([]);
    setSelectedRecommendedFilters(new Map());
    
    setIsGettingData(true);
    if (currentNode) {
      await handleGetDataForNode(currentNode.id);
    }
    setIsGettingData(false);
  };

  return (
    <Drawer
      isOpen={configurator.isOpen}
      placement="right"
      onClose={configurator.onClose}
      size="md"
    >
      <DrawerContent mt="50px">
        <DrawerBody>
          {currentNode ? (
            <Stack spacing={1}>
              <FormControl>
                <FormLabel mb={0}>Step Label</FormLabel>
                <Input
                  value={nodeName}
                  onChange={(e) => {
                    setNodeName(e.target.value);
                  }}
                  placeholder="Enter step label"
                />
              </FormControl>

              <Box>
                {/* Metric Selection Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={3}
                  borderColor="teal.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="teal.600">
                    Add Metrics
                  </Text>
                  
                  {/* Metric Dropdown */}
                  <FormControl>
                    <FormLabel>Select Metric</FormLabel>
                    <Menu closeOnSelect={true}>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        w="100%"
                        textAlign="left"
                        variant="outline"
                        colorScheme="teal"
                        isDisabled={availableMetrics.length === 0}
                      >
                        {selectedMetric
                          ? selectedMetric.header?.name || "Unnamed Metric"
                          : availableMetrics.length === 0
                          ? "No metrics available"
                          : "Select a metric"}
                      </MenuButton>
                      <Portal>
                        <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                          {availableMetrics.map((metric, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleSelectMetric(metric)}
                              _hover={{ bg: "teal.50" }}
                              isDisabled={isRecommendedMetric(metric.header?.id || "")}
                            >
                              <Flex alignItems="center" width="100%">
                                {metric.header?.name || "Unnamed Metric"}
                              </Flex>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </FormControl>

                  {/* Display Recommended Metrics */}
                  {recommendedMetrics.length > 0 && (
                    <Box mt={3} mb={2} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                      <Text fontWeight="medium" fontSize="sm" mb={1}>
                        Any one of the following metrics from Parent Recommendations will be picked:
                      </Text>
                      <Stack spacing={1}>
                        {recommendedMetrics.map((metric, index) => (
                          <Flex
                            key={index}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            alignItems="center"
                            backgroundColor="white"
                          >
                            <Box overflow="hidden" textOverflow="ellipsis">
                              <Text fontSize="sm" mb={0} fontWeight="medium">
                                {metric.header?.name || "Unnamed Metric"}
                              </Text>
                            </Box>
                            <Tag size="sm" colorScheme="green" ml="auto">
                              Add from parent
                            </Tag>
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Display Selected Metrics */}
                  {selectedMetrics?.length > 0 && (
                    <Box mt={1}>
                      <Text fontWeight="medium" mb={1}>
                        Selected Metrics:
                      </Text>
                      <Stack spacing={2}>
                        {selectedMetrics.map((metric) => (
                          <Flex
                            key={metric.header?.id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            justifyContent="space-between"
                            alignItems="center"
                            backgroundColor="teal.50"
                          >
                            <Text fontWeight="bold" fontSize="sm" mb={0}>
                              {metric.header?.name}
                            </Text>
                            <IconButton
                              aria-label="Remove metric"
                              icon={<CloseIcon />}
                              size="xs"
                              onClick={() => handleRemoveMetric(metric.header?.id || "")}
                              variant="ghost"
                              colorScheme="red"
                            />
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>

                {/* Column Selection Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={3}
                  borderColor="blue.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    Add Columns
                  </Text>
                  
                  {/* Column Dropdown */}
                  <FormControl>
                    <FormLabel>Select Column</FormLabel>
                    <Menu closeOnSelect={true}>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        w="100%"
                        textAlign="left"
                        variant="outline"
                        colorScheme="blue"
                        isDisabled={nonMetricColumns.length === 0}
                      >
                        {selectedColumn
                          ? selectedColumn.name || "Unnamed Column"
                          : nonMetricColumns.length === 0
                          ? "No columns available"
                          : "Select a column"}
                      </MenuButton>
                      <Portal>
                        <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                          {nonMetricColumns?.map((column, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleSelectColumn(column)}
                              _hover={{ bg: "blue.50" }}
                              isDisabled={
                                selectedColumns.some(c => c.column_id === column.column_id) ||
                                isRecommendedColumn(column.column_id || "")
                              }
                            >
                              {column.kg_column_header?.name}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </FormControl>

                  {/* Display Recommended Columns */}
                  {recommendedColumns.length > 0 && (
                    <Box mt={3} mb={2} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                      <Text fontWeight="medium" fontSize="sm" mb={1}>
                        Any one of the following columns from Parent Recommendations will be picked:
                      </Text>
                      <Stack spacing={1}>
                        {recommendedColumns?.map((column, index) => (
                          <Flex
                            key={index}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            alignItems="center"
                            backgroundColor="white"
                          >
                            <Box overflow="hidden" textOverflow="ellipsis">
                              <Text fontSize="sm" mb={0} fontWeight="medium">
                                {column.kg_column_header?.name || "Unnamed Column"}
                              </Text>
                            </Box>
                            <Tag size="sm" colorScheme="green" ml="auto">
                              Add from parent
                            </Tag>
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Display Selected Columns */}
                  {selectedColumns?.length > 0 && (
                    <Box mt={1}>
                      <Text fontWeight="medium" mb={1}>
                        Selected Columns:
                      </Text>
                      <Stack spacing={1}>
                        {selectedColumns?.map((column) => (
                          <Box
                            key={column.column_id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            backgroundColor="blue.50"
                          >
                            <Flex justifyContent="space-between" alignItems="center" mb={column?.kg_column_header?.column_type === ColumnType.TEMPORAL ? 2 : 0}>
                              <Text fontWeight="bold" fontSize="sm" mb={0}>
                                {column?.kg_column_header?.name}
                              </Text>
                              <IconButton
                                aria-label="Remove column"
                                icon={<CloseIcon />}
                                size="xs"
                                onClick={() => handleRemoveColumn(column?.column_id || "")}
                                variant="ghost"
                                colorScheme="red"
                              />
                            </Flex>
                            
                            {/* Inline time bucket selection for temporal columns */}
                            {column?.kg_column_header?.column_type === ColumnType.TEMPORAL && (
                              <Box>
                                <Text fontSize="xs" color="gray.600" mb={0} mt={-3}>
                                  Time Bucket:
                                </Text>
                                  <Flex gap={1} flexWrap="wrap">
                                   {Object?.entries(TimeBucket)
                                     ?.filter(([key, value]) => typeof value === "number" && value !== TimeBucket.UNRECOGNIZED)
                                     .map(([key, value]) => (
                                       <Button
                                         key={key}
                                         size="xs"
                                         variant={column.time_bucket === value ? "solid" : "outline"}
                                         colorScheme="blue"
                                         onClick={() => handleTimeBucketChange(column?.column_id || "", value as TimeBucket)}
                                       >
                                         {formatToSentenceCase(key)}
                                       </Button>
                                     ))}
                                 </Flex>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>

                {/* Add Benchmarks along with calculations */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={4}
                  borderColor="blue.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    Add Benchmarks
                  </Text>
                  <KGBenchmarkConfig
                    currentNode={currentNode}
                    selectedBenchmarks={selectedBenchmarks}
                    setSelectedBenchmarks={setSelectedBenchmarks}
                    analysisColumns={analysisColumns}
                    setAnalysisColumns={setAnalysisColumns}
                    selectedColumns={selectedColumns}
                  />
                </Box>

                {/* Add Filters Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={4}
                  borderColor="blue.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    Add Filters
                  </Text>
                  <KGAdditionalContext
                    currentNodeId={currentNode.id}
                    filters={currentNode.selected_filters || []}
                    setFilters={setFilters}
                    nonBenchmarkEdges={currentNode?.non_benchmarks}
                    temporalColumns={currentNode?.temporal_columns}
                    recommendedFilters={recommendedFilters}
                    handlePropogateFilterClick={handlePropogateFilterClick}
                    availableNodes={availableDescendants}
                    setAvailableNodes={setAvailableDescendants}
                    filterToPropagate={filterToPropagate}
                    setFilterToPropagate={setFilterToPropagate}
                  />
                </Box>

                {/* Recommendation Section */}
                {/* <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={4}
                  borderColor="blue.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    Add Recommendations
                  </Text>
                  <KGRecommendations
                    currentNode={currentNode}
                    addChildToRecommendation={handleAddChildNode}
                   />
                </Box> */}

                {/* Recommended Filters Section - Show only when Get Data is clicked and filters exist */}
                {showRecommendedFilters && recommendedFiltersToApply.length > 0 && (
                  <Box
                    borderWidth="2px"
                    borderRadius="md"
                    mb={4}
                    p={4}
                    borderColor="orange.300"
                    boxShadow="md"
                    bg="orange.50"
                  >
                    <Text fontWeight="bold" fontSize="lg" mb={3} color="orange.600">
                      Configure Recommended Filters
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Select values for each recommended filter below
                    </Text>
                    
                    <Stack spacing={4}>
                      {recommendedFiltersToApply.map((filter, index) => {
                        const columnId = filter.kg_column_header?.id;
                        const selected = columnId ? selectedRecommendedFilters.get(columnId) || [] : [];

                        return (
                          <Box
                            key={index}
                            borderWidth="1px"
                            borderRadius="lg"
                            p={3}
                            bg="white"
                            boxShadow="sm"
                          >
                            <VStack align="start" spacing={3}>
                              {/* Filter Header */}
                              <Text fontSize="md" fontWeight="semibold" mb={0}>
                                {filter?.kg_column_header?.name || "Unknown Column"}
                              </Text>

                              {/* Selected Values Display */}
                              {selected.length > 0 && (
                                <Box width="100%">
                                  <HStack spacing={2} wrap="wrap">
                                    {selected.map((value, idx) => (
                                      <Tag
                                        key={idx}
                                        size="sm"
                                        borderRadius="full"
                                        variant="solid"
                                        colorScheme="blue"
                                      >
                                        <TagLabel>{value}</TagLabel>
                                        <TagCloseButton
                                          onClick={() => {
                                            if (columnId) {
                                              const newSelected = selected.filter(v => v !== value);
                                              const newMap = new Map(selectedRecommendedFilters);
                                              newMap.set(columnId, newSelected);
                                              setSelectedRecommendedFilters(newMap);
                                            }
                                          }}
                                        />
                                      </Tag>
                                    ))}
                                  </HStack>
                                </Box>
                              )}

                              {/* Values Selection */}
                              <FormControl width="100%">
                                <ColumnValues
                                  contextValues={columnId ? recommendedFilterValues.get(columnId) || [] : []}
                                  selectedOptions={selected}
                                  setSelectedOptions={(newValues: string[]) => {
                                    if (columnId) {
                                      const newMap = new Map(selectedRecommendedFilters);
                                      newMap.set(columnId, newValues);
                                      setSelectedRecommendedFilters(newMap);
                                    }
                                  }}
                                />
                              </FormControl>
                            </VStack>
                          </Box>
                        );
                      })}
                    </Stack>

                    {/* Action Buttons */}
                    <HStack spacing={3} mt={4} justify="flex-end">
                      <Button
                        variant="ghost"
                        onClick={handleSkipRecommendedFilters}
                        size="sm"
                      >
                        Skip & Get Data
                      </Button>
                      <Button
                        colorScheme="orange"
                        onClick={handleApplyRecommendedFilters}
                        size="sm"
                        isDisabled={Array.from(selectedRecommendedFilters.values()).every(
                          (values) => values.length === 0
                        )}
                      >
                        Apply Filters & Get Data
                      </Button>
                    </HStack>
                  </Box>
                )}

              </Box>
            </Stack>
          ) : (
            <Text>No node selected</Text>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={configurator.onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="orange" 
            mr={3} 
            onClick={handleGetData}
            isLoading={isGettingData}
            loadingText="Getting Data..."
            isDisabled={isGettingData || !currentNode || showRecommendedFilters}
          >
            {showRecommendedFilters ? "Configure Filters Below" : "Get Data"}
          </Button>
          {/* <Button colorScheme="blue" onClick={() => handleSave()}>
            Save
          </Button> */}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
export default KGNodeConfigurator;